const API_ROOT = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '')

export class ApiError extends Error {
  constructor(message, status = 0, details = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

function queryString(params = {}) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value)
    }
  })
  const serialized = query.toString()
  return serialized ? `?${serialized}` : ''
}

async function request(path, options = {}, unwrap = true) {
  const headers = new Headers(options.headers)
  if (options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${API_ROOT}${path}`, {
    credentials: 'same-origin',
    ...options,
    headers,
  })

  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json')
    ? await response.json()
    : null

  if (!response.ok) {
    const apiError = payload?.error
    const message = typeof apiError === 'string'
      ? apiError
      : apiError?.message || payload?.message || `Ошибка запроса (${response.status})`
    if (response.status === 401 && path !== '/auth/login' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('kurochkino:unauthorized'))
    }
    throw new ApiError(message, response.status, apiError || payload)
  }

  // API uses { data: ... }. Keeping the fallback makes the UI tolerant while
  // the backend contract evolves.
  return unwrap && payload && Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : payload
}

function body(value) {
  return { body: JSON.stringify(value) }
}

async function listAllBookings(filters = {}) {
  const { limit: _limit, offset: _offset, ...stableFilters } = filters
  const pageSize = 200
  const collected = []
  let offset = 0

  while (true) {
    const envelope = await request(
      `/bookings${queryString({ ...stableFilters, limit: pageSize, offset })}`,
      {},
      false,
    )
    const data = envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
      ? envelope.data
      : envelope
    const batch = asList(data, ['bookings'])
    collected.push(...batch)

    const total = Number(envelope?.meta?.total)
    if (batch.length === 0) break
    offset += batch.length
    if (Number.isFinite(total) ? offset >= total : batch.length < pageSize) break
  }

  return collected
}

async function listAllInquiries(filters = {}) {
  const pageSize = 100
  const collected = []
  let offset = 0

  while (true) {
    const envelope = await request(
      `/inquiries${queryString({ ...filters, limit: pageSize, offset })}`,
      {},
      false,
    )
    const data = envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
      ? envelope.data
      : envelope
    const batch = asList(data, ['inquiries'])
    collected.push(...batch)

    const total = Number(envelope?.meta?.total)
    if (batch.length === 0) break
    offset += batch.length
    if (Number.isFinite(total) ? offset >= total : batch.length < pageSize) break
  }

  return collected
}

export const api = {
  auth: {
    me: () => request('/auth/me'),
    login: (credentials) => request('/auth/login', { method: 'POST', ...body(credentials) }),
    logout: () => request('/auth/logout', { method: 'POST', ...body({}) }),
  },
  users: {
    list: () => request('/users'),
    createOperator: (operator) => request('/users', { method: 'POST', ...body(operator) }),
  },
  resources: {
    list: (filters) => request(`/resources${queryString(filters)}`),
    get: (id) => request(`/resources/${id}`),
    create: (resource) => request('/resources', { method: 'POST', ...body(resource) }),
    update: (id, resource) => request(`/resources/${id}`, { method: 'PATCH', ...body(resource) }),
    getIcal: (id) => request(`/resources/${id}/ical`),
    updateIcal: (id, settings) => request(`/resources/${id}/ical`, { method: 'PATCH', ...body(settings) }),
  },
  ical: {
    blocks: (filters) => request(`/ical/blocks${queryString(filters)}`),
  },
  availability: {
    search: (filters) => request(`/availability${queryString(filters)}`),
    resource: (id, filters) => request(`/resources/${id}/availability${queryString(filters)}`),
  },
  bookings: {
    list: (filters) => request(`/bookings${queryString(filters)}`),
    listAll: (filters) => listAllBookings(filters),
    get: (id) => request(`/bookings/${id}`),
    create: (booking) => request('/bookings', { method: 'POST', ...body(booking) }),
    update: (id, booking) => request(`/bookings/${id}`, { method: 'PATCH', ...body(booking) }),
    cancel: (id) => request(`/bookings/${id}/cancel`, { method: 'POST', ...body({}) }),
    history: (id) => request(`/bookings/${id}/history`),
  },
  catalog: {
    list: (filters) => request(`/catalog${queryString(filters)}`),
    create: (item) => request('/catalog', { method: 'POST', ...body(item) }),
    update: (id, item) => request(`/catalog/${id}`, { method: 'PATCH', ...body(item) }),
    movements: (id) => request(`/catalog/${id}/movements`),
    move: (id, movement) => request(`/catalog/${id}/movements`, { method: 'POST', ...body(movement) }),
  },
  sales: {
    list: (filters) => request(`/sales${queryString(filters)}`),
    create: (sale) => request('/sales', { method: 'POST', ...body(sale) }),
    cancel: (id, reason) => request(`/sales/${id}/cancel`, { method: 'POST', ...body({ reason }) }),
  },
  inquiries: {
    listAll: (filters) => listAllInquiries(filters),
    get: (id) => request(`/inquiries/${id}`),
    updateStatus: (id, status) => request(`/inquiries/${id}`, { method: 'PATCH', ...body({ status }) }),
    recordContact: (id) => request(`/inquiries/${id}/contact`, { method: 'POST', ...body({}) }),
    convert: (id, booking) => request(`/inquiries/${id}/convert`, { method: 'POST', ...body(booking) }),
  },
  dashboard: {
    get: (date) => request(`/dashboard${queryString({ date })}`),
  },
}

export function asList(payload, aliases = []) {
  if (Array.isArray(payload)) return payload
  for (const key of ['items', 'results', ...aliases]) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}
